import React from 'react';
import { useTranslation } from 'react-i18next';
import { KoobInputCheckbox } from '@koob/ui';
import { KoobBadge } from '@koob/ui';
import { PromotionKindEnum } from 'utils';
import { PromotionAppliesToEnum } from 'utils/promotions';

// Map a promotion state to a badge color.
const STATE_BADGE_COLOR = {
  operational: 'green',
  draft: 'gray',
  archived: 'red',
};

// Map a promotion kind (deal type) to its translation key.
const PROMOTION_KIND_LABEL_KEY = {
  basic_deal: 'promotionForm:deals.basicDeal',
  early_booker: 'promotionForm:deals.earlyBooker',
  last_minute: 'promotionForm:deals.lastMinute',
  stay_flexibility: 'promotionForm:deals.stayFlexibility',
};

export default function PromotionCard({ promo, isSelected, onToggleSelect }) {
  const { t } = useTranslation([
    'toConnectionRequest',
    'promotionForm',
    'promotions',
  ]);

  const getPromoIcon = (typeSlug) => {
    const style = 'w-5 h-5 text-orange-500';

    switch (typeSlug) {
      case PromotionKindEnum.FREE_NIGHT:
        return <i className={`fa-light fa-moon ${style}`} />;
      case PromotionKindEnum.AMOUNT:
        return <i className={`fa-light fa-bolt ${style}`} />;
      case PromotionKindEnum.SUPPLEMENT:
        return <i className={`fa-light fa-sun ${style}`} />;
      default:
        return <i className={`fa-light fa-calendar ${style}`} />;
    }
  };

  const typeLabel = promo.typeSlug
    ? t(`promotionForm:promotionTypes.${promo.typeSlug}`)
    : '—';

  const productDisplay = (promo) => {
    return (
      <div className="flex items-center gap-1.5">
        <i
          className={[
            promo.isExperience ? 'far fa-layer-group' : 'far fa-bed',
            'text-blue-500',
          ].join(' ')}
        />
        {promo.productName ?? '—'}
      </div>
    );
  };

  return (
    <div
      onClick={() => onToggleSelect(promo)}
      className={[
        'bg-white border border-l-4 rounded-md transition-all cursor-pointer select-none',
        isSelected
          ? 'border-l-k-orange border-orange-300 bg-orange-50/10'
          : 'border-l-k-gray border-gray-300 hover:border-orange-200',
      ].join(' ')}
    >
      <div className="flex gap-4 px-5 py-5">
        {/* Checkbox (click handled by the whole card; stop double-toggle here) */}
        <div className="pt-1" onClick={(e) => e.stopPropagation()}>
          <KoobInputCheckbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(promo)}
          />
        </div>

        {/* Icon */}
        <div className="pt-1">{getPromoIcon(promo.typeSlug)}</div>

        {/* Main content */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">{promo.name}</h3>
            </div>

            <div className="flex gap-2">
              {/* Distributing a DMC offer extends it to the TO. */}
              <KoobBadge
                color={
                  promo.appliesTo === PromotionAppliesToEnum.NOT_SET
                    ? 'red'
                    : 'blue'
                }
              >
                {t(`promotions:appliesTo.${promo.appliesTo}`)}
              </KoobBadge>
              {promo.state && (
                <KoobBadge color={STATE_BADGE_COLOR[promo.state] ?? 'gray'}>
                  {t(`promotionForm:states.${promo.state}`)}
                </KoobBadge>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-4 gap-8 mt-6">
            <div>
              <p className="text-xs text-gray-500">
                {t('promotions.card.kind')}
              </p>
              <p className="font-semibold text-sm">
                {promo.kind
                  ? t(PROMOTION_KIND_LABEL_KEY[promo.kind] ?? promo.kind)
                  : '—'}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">
                {t('promotions.card.benefits')}
              </p>
              <p className="font-semibold text-sm text-orange-600">
                {promo.discount}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">
                {promo.isExperience
                  ? t('promotions.card.experienceName')
                  : t('promotions.card.hotelName')}
              </p>
              <p className="font-semibold text-sm">{productDisplay(promo)}</p>
            </div>

            <div className="text-right shrink-0">
              <div className="uppercase tracking-wider text-[10px] text-gray-400 font-semibold">
                {t('promotions.card.promotionType')}
              </div>
              <div className="font-bold text-gray-900">{typeLabel}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
